import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as Auth from "../store/ducks/auth.duck";
import { Button, Form, Row, Col } from "react-bootstrap";
import { Grid, Typography, List, ListItem, ListItemText } from "@material-ui/core";
import { useReactToPrint } from "react-to-print";

export default function MFAForm(props) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(Auth.actions.getMFA());
  }, [dispatch]);

  const mfaQR = useSelector((state) => state.auth.MFAQR);
  const codeAccepted = useSelector((state) => state.auth.CodeAccepted);
  const backupCodes = useSelector((state) => state.auth.BackupCodes);
  const userEmail = useSelector((state) => state.auth.email);
  const emailCodeAccepted = useSelector((state) => state.auth.emailCodeAccepted);
  const emailCodeSent = useSelector((state) => state.auth.emailCodeSent);

  // 1 = choose method
  // 2 = configure selected method
  // 3 = backup codes
  const [step, setStep] = useState(1);

  // "Authenticator" or "Email"
  const [verificationMethod, setVerificationMethod] = useState("Authenticator");

  const [confirmationCode, setConfirmationCode] = useState("");
  const [confirmationCodeHelpText, setConfirmationCodeHelpText] = useState("");

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  /*
   * STEP 1
   * Choose verification method
   */
  function selectVerificationMethod(method) {
    setVerificationMethod(method);
    setConfirmationCode("");
    setConfirmationCodeHelpText("");
  }

  function nextFromMethodSelection() {
    setConfirmationCode("");
    setConfirmationCodeHelpText("");

    if (verificationMethod === "Authenticator") {
      setStep(2);
    }

    if (verificationMethod === "Email") {
      setStep(2);

      // If you need to send the email code here,
      // dispatch that action here.
      //
      // Example:
      // dispatch(Auth.actions.postSendEmailMFACode());
    }
  }

  /*
   * STEP 2
   * Confirm the six digit code
   */
  function confirmMFA() {
    if (!confirmationCode || confirmationCode.length !== 6) {
      setConfirmationCodeHelpText("Please enter the six digit code");
      return;
    }

    setConfirmationCodeHelpText("");

    if (verificationMethod === "Authenticator") {
      dispatch(
        Auth.actions.postConfirmCode({
          confirmCode: confirmationCode,
          type: verificationMethod,
        }),
      );
    }

    if (verificationMethod === "Email") {
      dispatch(
        Auth.actions.postConfirmEmailCode({
          confirmCode: confirmationCode,
        }),
      );
    }
  }
  /*
   * When the Redux state says the code was accepted,
   * move to backup codes.
   */
  useEffect(() => {
    if (step === 2 && verificationMethod === "Authenticator" && codeAccepted === true) {
      setStep(3);
    }

    if (step === 2 && verificationMethod === "Email" && emailCodeAccepted === true) {
      setStep(3);
    }
  }, [codeAccepted, emailCodeAccepted, verificationMethod, step]);
  /*
   * STEP 3
   * Finish MFA setup
   */
  function activateMFA() {
    dispatch(
      Auth.actions.postActivateMFA({
        verificationMethod: verificationMethod,
      }),
    );

    props.onCloseMFA();
  }

  /*
   * Split backup codes into two columns
   */
  const firstHalf = backupCodes ? backupCodes.slice(0, Math.ceil(backupCodes.length / 2)) : [];

  const secondHalf = backupCodes ? backupCodes.slice(Math.ceil(backupCodes.length / 2)) : [];

  return (
    <>
      {/* ========================================================= */}
      {/* STEP 1 - CHOOSE VERIFICATION METHOD                       */}
      {/* ========================================================= */}

      {step === 1 && (
        <>
          <Row>
            <Col md="12">
              <p>Choose how you would like to receive your verification codes.</p>

              {/* Authenticator */}
              <div
                onClick={() => selectVerificationMethod("Authenticator")}
                style={{
                  border: verificationMethod === "Authenticator" ? "1px solid #5267ff" : "1px solid #eeeeee",
                  padding: "12px",
                  marginBottom: "8px",
                  cursor: "pointer",
                }}
              >
                <Form.Check
                  type="radio"
                  id="mfa-authenticator"
                  name="mfa-method"
                  label={
                    <>
                      <strong>Authenticator app</strong>
                      <br />
                      <small>Use a two-factor authentication app such as Microsoft Authenticator.</small>
                    </>
                  }
                  checked={verificationMethod === "Authenticator"}
                  onChange={() => selectVerificationMethod("Authenticator")}
                />
              </div>

              {/* Email */}
              <div
                onClick={() => selectVerificationMethod("Email")}
                style={{
                  border: verificationMethod === "Email" ? "1px solid #5267ff" : "1px solid #eeeeee",
                  padding: "12px",
                  marginBottom: "15px",
                  cursor: "pointer",
                }}
              >
                <Form.Check
                  type="radio"
                  id="mfa-email"
                  name="mfa-method"
                  label={
                    <>
                      <strong>Email</strong>
                      <br />
                      <small>Receive a six-digit code at your login email address.</small>
                    </>
                  }
                  checked={verificationMethod === "Email"}
                  onChange={() => selectVerificationMethod("Email")}
                />
              </div>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md="12">
              <Grid container spacing={0} justifyContent="space-between">
                <Button variant="light" onClick={() => props.onCloseMFA()}>
                  Cancel
                </Button>

                <Button variant="success" size="sm" onClick={nextFromMethodSelection}>
                  Next
                </Button>
              </Grid>
            </Col>
          </Row>
        </>
      )}

      {/* ========================================================= */}
      {/* STEP 2a - AUTHENTICATOR APP                              */}
      {/* ========================================================= */}

      {step === 2 && verificationMethod === "Authenticator" && (
        <>
          <Row>
            <Col md="12">
              <p>Scan the QR code below with the two-factor authentication app on your phone.</p>

              {mfaQR && mfaQR.length > 5 ? (
                <div
                  style={{
                    marginTop: 10,
                    padding: 5,
                    border: "solid 1px gray",
                    maxWidth: 160,
                  }}
                >
                  <img src={`data:image/png;base64,${mfaQR}`} style={{ maxHeight: 150 }} alt="MFA QR Code" />
                </div>
              ) : (
                <div style={{ marginTop: 15 }}>No QR code available</div>
              )}
            </Col>
          </Row>

          <Row style={{ marginTop: 15 }}>
            <Col md="12">
              <Form.Group>
                <Form.Label>Enter the six-digit code from the application</Form.Label>

                <Form.Text>After scanning the QR code, the app will display a six-digit code that you can enter below.</Form.Text>

                <Row style={{ marginTop: 10 }}>
                  <Col md="6">
                    <Form.Control
                      type="text"
                      name="confirmationCode"
                      value={confirmationCode}
                      onChange={(event) => {
                        setConfirmationCode(event.target.value);
                        setConfirmationCodeHelpText("");
                      }}
                      autoComplete="new-password"
                      maxLength={6}
                      style={{ borderColor: "#D1D1D1" }}
                    />

                    {confirmationCodeHelpText && <p className="error-help">{confirmationCodeHelpText}</p>}

                    {codeAccepted === false && (
                      <Form.Label
                        style={{
                          color: "red",
                          marginTop: 10,
                        }}
                      >
                        Code Not Valid - Try Again
                      </Form.Label>
                    )}
                  </Col>
                </Row>
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md="12">
              <Grid container spacing={0} justifyContent="space-between">
                <Button variant="light" onClick={() => props.onCloseMFA()}>
                  Cancel
                </Button>

                <Button variant="success" size="sm" onClick={confirmMFA}>
                  Next
                </Button>
              </Grid>
            </Col>
          </Row>
        </>
      )}

      {/* ========================================================= */}
      {/* STEP 2b - EMAIL                                           */}
      {/* ========================================================= */}

      {step === 2 && verificationMethod === "Email" && (
        <>
          <Row>
            <Col md="12">
              <p>A six-digit verification code will be sent to your login email address.</p>

              <Form.Group>
                <Form.Label>Email</Form.Label>

                <Row>
                  <Col md="6">
                    <Form.Control
                      type="email"
                      value={userEmail || ""}
                      disabled
                      style={{
                        maxWidth: 450,
                        backgroundColor: "#f5f5f5",
                      }}
                    />
                  </Col>

                  <Col md="3" className="d-flex align-items-end">
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        dispatch(Auth.actions.postSendEmailMFACode());
                      }}
                    >
                      Send code
                    </Button>
                  </Col>
                </Row>

                <Form.Text>This is your primary login email and cannot be changed here.</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row style={{ marginTop: 15 }}>
            <Col md="12">
              <Form.Group>
                <Form.Label>Enter the six-digit code sent to your email</Form.Label>

                <Row>
                  <Col md="6">
                    <Form.Control
                      type="text"
                      name="confirmationCode"
                      value={confirmationCode}
                      onChange={(event) => {
                        setConfirmationCode(event.target.value);
                        setConfirmationCodeHelpText("");
                      }}
                      autoComplete="new-password"
                      maxLength={6}
                      style={{ borderColor: "#D1D1D1" }}
                    />
                  </Col>
                </Row>

                {confirmationCodeHelpText && <p className="error-help">{confirmationCodeHelpText}</p>}

                {codeAccepted === false && (
                  <Form.Label
                    style={{
                      color: "red",
                      marginTop: 10,
                    }}
                  >
                    Code Not Valid - Try Again
                  </Form.Label>
                )}
              </Form.Group>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md="12">
              <Grid container spacing={0} justifyContent="space-between">
                <Button variant="light" onClick={() => props.onCloseMFA()}>
                  Cancel
                </Button>

                <Button variant="success" size="sm" onClick={confirmMFA}>
                  Next
                </Button>
              </Grid>
            </Col>
          </Row>
        </>
      )}

      {/* ========================================================= */}
      {/* STEP 3 - BACKUP CODES                                    */}
      {/* ========================================================= */}

      {step === 3 && (
        <>
          <Row>
            <Col md="12">
              <p style={{ fontWeight: 600 }}>Backup Codes</p>

              <p>
                Backup codes are used to access your account in the event that you cannot receive two-factor authentication. For security reasons, each code can
                only be used once.
              </p>

              <p style={{ fontWeight: 600 }}>Print or copy your Backup Codes before continuing two-factor authentication setup.</p>
            </Col>
          </Row>

          <Row
            style={{
              marginTop: 10,
              marginBottom: 20,
              padding: 5,
              border: "solid 1px gray",
            }}
            ref={componentRef}
          >
            <Col md="6">
              <List>
                {firstHalf.map((row, index) => (
                  <ListItem key={index} dense>
                    <ListItemText disableTypography primary={<Typography>{row}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </Col>

            <Col md="6">
              <List>
                {secondHalf.map((row, index) => (
                  <ListItem key={index} dense>
                    <ListItemText disableTypography primary={<Typography>{row}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Grid container spacing={0}>
                <Button variant="warning" onClick={handlePrint}>
                  Print
                </Button>

                <Button variant="secondary" size="sm" onClick={() => navigator.clipboard.writeText(backupCodes.join("\n"))} style={{ marginLeft: 20 }}>
                  Copy
                </Button>
              </Grid>
            </Col>
          </Row>

          <hr />

          <Row>
            <Col md="12">
              <Grid container spacing={0} justifyContent="space-between">
                <Button variant="light" onClick={() => props.onCloseMFA()}>
                  Cancel
                </Button>

                <Button variant="success" size="sm" onClick={activateMFA}>
                  Finish
                </Button>
              </Grid>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
